import React from 'react'
import {
  BoltIcon,
  ChevronRightIcon,
  DocumentDuplicateIcon,
  ShieldCheckIcon
} from '@heroicons/react/24/outline'
import Card from '../ui/Card'
import Badge from '../ui/Badge'
import Button from '../ui/Button'
import { pqrSummaries, projects, wizardSteps } from '../../data/dummyData'

const fieldGroups = [
  {
    title: 'Base Material & Joint Design',
    fields: [
      { label: 'Material Grade', value: 'API 5L X60 (Group 1)' },
      { label: 'Thickness Range', value: '8 mm - 25 mm' },
      { label: 'Joint Type', value: 'Single V, 60° bevel' },
      { label: 'Root Gap', value: '3 mm (min)' }
    ]
  },
  {
    title: 'Welding Process & Parameters',
    fields: [
      { label: 'Process', value: 'GMAW + FCAW' },
      { label: 'Current / Voltage', value: '220-260 A / 24-28 V' },
      { label: 'Travel Speed', value: '180-240 mm/min' },
      { label: 'Heat Input', value: '0.9-1.3 kJ/mm' }
    ]
  },
  {
    title: 'Thermal & Positioning',
    fields: [
      { label: 'Position', value: '5G / 6G' },
      { label: 'Preheat', value: '120-150 C' },
      { label: 'Interpass', value: '<= 230 C' },
      { label: 'PWHT', value: 'Not required' }
    ]
  },
  {
    title: 'Shielding Gas & Electrical',
    fields: [
      { label: 'Gas Composition', value: 'Ar 80% / CO2 20%' },
      { label: 'Gas Flow', value: '18-22 L/min' },
      { label: 'Polarity', value: 'DCEP' },
      { label: 'Power Source', value: 'CV' }
    ]
  }
]

export default function WpsWizard() {
  const activeProject = projects[0]

  return (
    <div className="space-y-6">
      <Card title="WPS Creation Wizard">
        <div className="grid gap-6 lg:grid-cols-3">
          <div className="space-y-4">
            <p className="text-sm text-gray-500">Process path</p>
            <div className="space-y-3">
              {wizardSteps.map((step, index) => (
                <div key={step.title} className="flex items-start gap-3">
                  <div className={`mt-1 h-4 w-4 rounded-full border-2 ${step.completed ? 'border-success-500 bg-success-500' : 'border-gray-300'}`} />
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-semibold text-gray-900">{step.title}</p>
                      {index === 2 && <Badge tone="warning">In progress</Badge>}
                    </div>
                    <p className="text-xs text-gray-500">{step.description}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="rounded-lg border border-dashed border-gray-300 p-4">
              <p className="text-xs text-gray-500">Active project</p>
              <p className="text-sm font-semibold text-gray-900">{activeProject.name}</p>
              <p className="text-xs text-gray-500">Pipe sizes {activeProject.pipeSizeRange}</p>
            </div>
          </div>

          <div className="lg:col-span-2 space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label className="text-xs font-medium text-gray-500">WPS Number</label>
                <input className="input-primary mt-1" value="WPS-PL-0021" readOnly />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500">Applicable Code</label>
                <input className="input-primary mt-1" value="ASME B31.3 / API 1104" readOnly />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500">Pipe Size Category</label>
                <select className="input-primary mt-1">
                  <option>12 in - 24 in</option>
                  <option>24 in - 36 in</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500">Joint Configuration</label>
                <select className="input-primary mt-1">
                  <option>Single V, 60°</option>
                  <option>Double V, 70°</option>
                  <option>U Groove</option>
                </select>
              </div>
            </div>

            <div className="rounded-lg bg-primary-50 p-4">
              <div className="flex items-center gap-3">
                <BoltIcon className="h-5 w-5 text-primary-600" />
                <div>
                  <p className="text-sm font-semibold text-primary-700">Auto-filled 18 parameters</p>
                  <p className="text-xs text-primary-700">Matched to PQR-402 based on material group and thickness.</p>
                </div>
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              {fieldGroups.map((group) => (
                <div key={group.title} className="rounded-lg border border-gray-200 p-4">
                  <p className="text-sm font-semibold text-gray-900">{group.title}</p>
                  <div className="mt-3 space-y-2 text-xs text-gray-600">
                    {group.fields.map((field) => (
                      <div key={field.label} className="flex items-center justify-between">
                        <span>{field.label}</span>
                        <span className="font-medium text-gray-900">{field.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div className="flex flex-wrap gap-3">
              <Button variant="secondary">
                <DocumentDuplicateIcon className="h-4 w-4 mr-2" />
                Clone Existing WPS
              </Button>
              <Button variant="secondary">
                <ShieldCheckIcon className="h-4 w-4 mr-2" />
                Run Validation
              </Button>
              <Button>
                Continue to Floor Sheet
                <ChevronRightIcon className="h-4 w-4 ml-2" />
              </Button>
            </div>
          </div>
        </div>
      </Card>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card title="Qualified PQR Library" action={<Button variant="ghost">View all</Button>}>
          <div className="space-y-3">
            {pqrSummaries.map((pqr) => (
              <div key={pqr.id} className="flex items-start justify-between rounded-lg border border-gray-200 p-3">
                <div>
                  <p className="text-sm font-semibold text-gray-900">{pqr.number}</p>
                  <p className="text-xs text-gray-500">{pqr.process} • {pqr.baseMaterial}</p>
                  <p className="text-xs text-gray-400">Qualified range {pqr.qualifiedRange}</p>
                </div>
                <Button variant="secondary" className="text-xs">Apply</Button>
              </div>
            ))}
          </div>
        </Card>

        <Card title="Revision & Approval">
          <div className="space-y-4">
            <div className="rounded-lg border border-gray-200 p-3">
              <p className="text-xs text-gray-500">Current revision</p>
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold text-gray-900">Revision A</p>
                <Badge tone="warning">Draft</Badge>
              </div>
              <p className="text-xs text-gray-400">Owner: Priya S. • Updated 2 days ago</p>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500">Reviewers</label>
              <div className="mt-2 flex flex-wrap gap-2">
                <Badge tone="gray">QA Lead</Badge>
                <Badge tone="gray">Welding Eng</Badge>
                <Badge tone="gray">Client Rep</Badge>
              </div>
            </div>
            <Button className="w-full">Send for Review</Button>
          </div>
        </Card>
      </div>
    </div>
  )
}
